import React from 'react';
import { cn } from '@/lib/utils';
import { Clock, User, BookOpen, AlertTriangle, Check, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const DAYS = [
  { id: 'monday', label: 'Segunda', short: 'SEG', color: 'bg-amber-500' },
  { id: 'tuesday', label: 'Terça', short: 'TER', color: 'bg-rose-500' },
  { id: 'wednesday', label: 'Quarta', short: 'QUA', color: 'bg-emerald-500' },
  { id: 'thursday', label: 'Quinta', short: 'QUI', color: 'bg-orange-500' },
  { id: 'friday', label: 'Sexta', short: 'SEX', color: 'bg-cyan-500' }
];

// Cores para disciplinas (geradas automaticamente baseado no nome)
const getSubjectColor = (subjectName) => {
  if (!subjectName) return 'bg-gray-100 text-gray-600';
  
  const colors = [
    'bg-blue-100 text-blue-700 border-blue-200',
    'bg-purple-100 text-purple-700 border-purple-200',
    'bg-green-100 text-green-700 border-green-200',
    'bg-yellow-100 text-yellow-700 border-yellow-200',
    'bg-pink-100 text-pink-700 border-pink-200',
    'bg-indigo-100 text-indigo-700 border-indigo-200',
    'bg-teal-100 text-teal-700 border-teal-200',
    'bg-orange-100 text-orange-700 border-orange-200',
    'bg-red-100 text-red-700 border-red-200',
    'bg-cyan-100 text-cyan-700 border-cyan-200',
  ];
  
  const hash = subjectName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const ScheduleCell = ({ schedule, slot, day, onCellClick, canEdit, hasConflict, viewMode = 'class' }) => {
  const isEmpty = !schedule;
  const subjectColor = getSubjectColor(schedule?.subject_name);
  
  // Na visualização por professor, mostra a turma em vez do professor
  const secondaryInfo = viewMode === 'teacher' ? schedule?.class_name : schedule?.employee_name;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <td
            onClick={() => canEdit && onCellClick(day, slot.id)}
            className={cn(
              "border p-0.5 text-center transition-all duration-200 relative",
              "min-w-[70px] sm:min-w-[100px] h-[50px] sm:h-[60px]",
              canEdit && "cursor-pointer",
              isEmpty && canEdit && "hover:bg-blue-50 group",
              hasConflict && "ring-2 ring-red-500 ring-inset"
            )}
          >
            {isEmpty ? (
              <div className="h-full w-full flex items-center justify-center">
                {canEdit && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                )}
              </div>
            ) : (
              <div className={cn(
                "h-full w-full rounded-md p-1 sm:p-1.5 flex flex-col justify-center border",
                subjectColor,
                canEdit && "hover:shadow-md hover:scale-[1.02] transition-transform active:scale-95"
              )}>
                {/* Na visualização por professor, mostra a turma primeiro */}
                {viewMode === 'teacher' && schedule?.class_name && (
                  <p className="font-bold text-[8px] sm:text-[10px] leading-tight truncate text-slate-800">
                    {schedule.class_name.split(' - ')[0]}
                  </p>
                )}
                <p className={cn(
                  "font-semibold leading-tight truncate",
                  viewMode === 'teacher' ? "text-[8px] sm:text-[10px]" : "text-[9px] sm:text-[11px]"
                )}>
                  {schedule.subject_name}
                </p>
                {viewMode !== 'teacher' && (
                  <p className="text-[8px] sm:text-[9px] opacity-75 truncate mt-0.5">
                    {secondaryInfo}
                  </p>
                )}
                {hasConflict && (
                  <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-500 absolute top-0.5 right-0.5" />
                )}
              </div>
            )}
          </td>
        </TooltipTrigger>
        {schedule && (
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              {viewMode === 'teacher' && schedule?.class_name && (
                <p className="font-bold text-sm">{schedule.class_name}</p>
              )}
              <p className="font-semibold">{schedule.subject_name}</p>
              <p className="text-sm flex items-center gap-1">
                <User className="w-3 h-3" />
                {schedule.employee_name}
              </p>
              <p className="text-sm flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {slot.label}
              </p>
              {hasConflict && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Conflito detectado!
                </p>
              )}
            </div>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

export default function ScheduleGrid({ 
  timeSlots, 
  schedules, 
  onCellClick, 
  canEdit = false,
  conflicts = [],
  className = '',
  turmaName = '',
  viewMode = 'class' // 'class' para visualização por turma, 'teacher' para visualização por professor
}) {
  const getScheduleForSlot = (day, timeSlot) => {
    return schedules.find(s => s.day === day && s.time_slot === timeSlot);
  };

  const hasConflict = (day, timeSlot, employeeId) => {
    return conflicts.some(c => 
      c.dia === day && 
      c.horario === timeSlot && 
      c.employee_id === employeeId
    );
  };

  return (
    <div className={cn("overflow-x-auto rounded-xl border shadow-sm scrollbar-hide", className)}>
      <table className="w-full border-collapse bg-white min-w-[500px]">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-gradient-to-r from-slate-700 to-slate-800 text-white p-2 sm:p-3 text-xs sm:text-sm font-semibold min-w-[60px] sm:min-w-[80px]">
              <div className="flex items-center justify-center gap-0.5 sm:gap-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Horário</span>
              </div>
            </th>
            {DAYS.map(day => (
              <th 
                key={day.id} 
                className={cn(
                  "p-1.5 sm:p-3 text-xs sm:text-sm font-semibold text-white min-w-[70px] sm:min-w-[120px]",
                  day.color
                )}
              >
                <div className="hidden sm:block">{day.label}</div>
                <div className="sm:hidden text-[10px]">{day.short}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {timeSlots.map((slot, index) => (
            <tr 
              key={slot.id} 
              className={cn(
                index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
              )}
            >
              <td className="sticky left-0 z-10 bg-inherit border p-1 sm:p-2 text-center">
                <div className="text-[9px] sm:text-xs font-medium text-slate-600 whitespace-nowrap">
                  <span className="hidden sm:inline">{slot.label}</span>
                  <span className="sm:hidden">{slot.id}</span>
                </div>
              </td>
              {DAYS.map(day => {
                const schedule = getScheduleForSlot(day.id, slot.id);
                const conflict = schedule && hasConflict(day.id, slot.id, schedule.employee_id);
                
                return (
                  <ScheduleCell
                    key={`${day.id}-${slot.id}`}
                    schedule={schedule}
                    slot={slot}
                    day={day.id}
                    onCellClick={onCellClick}
                    canEdit={canEdit}
                    hasConflict={conflict}
                    viewMode={viewMode}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
